<template>
    <div id="leave-general-page">
        <div class="top-buttons">
            <ui5-button
                id="cancelLeaveGeneral"
                @click="$router.push('/employees')">
                {{ trans('pages.leave_general.cancel') }}
            </ui5-button>
        </div>
        <div class="leftPane">
            <h2>{{ trans('pages.leave_general.general_leave_days') }}</h2>
            <table class="leave-list">
                <thead>
                    <tr>
                        <th class="nameHeading">{{ trans('pages.leave_general.name') }}</th>
                        <th class="dateHeading">{{ trans('pages.leave_general.date') }}</th>
                        <th class="actionHeading">{{ trans('pages.leave_general.actions') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="leaves.length == 0">
                        <td colspan="3">
                            {{ trans('pages.leave_general.no_leave_days', { year: selectedYear }) }}
                        </td>
                    </tr>
                    <template v-else>
                        <tr
                            :class="'leave_' + leave.id"
                            v-for="(leave, leaveIndex) in leaves"
                            :key="'leave.' + leave.id">
                            <td class="name">{{ leave.name }}</td>
                            <td class="date">{{ leave.day }}</td>
                            <td class="action">
                                <i
                                    @click="prepareUpdateForm(leave)"
                                    class="fa fa-pencil"
                                    :alt="trans('pages.leave_general.edit')"
                                    :title="trans('pages.leave_general.edit')" />
                                <i
                                    @click="deleteLeave(leave, leaveIndex)"
                                    class="fa fa-trash"
                                    :alt="trans('pages.leave_general.delete')"
                                    :title="trans('pages.leave_general.delete')" />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <div id="listControls">
                <ui5-button
                    id="previousDate"
                    @click="shiftDateForLeaveList(-1)">
                    {{ trans('pages.leave_general.previous') }}
                </ui5-button>
                {{ trans('pages.leave_general.leave_days_for') }}
                <span class="selectedYear">{{ selectedYear }}</span>

                <ui5-button id="nextDate" @click="shiftDateForLeaveList(+1)">
                    {{ trans('pages.leave_general.next') }}
                </ui5-button>
            </div>
            <p> </p>
            <div id="addLeaveGeneral">
                <h3>{{ trans('pages.leave_general.add_general_leave_day') }}</h3>
                <div class="field">
                    <ui5-label for="add-name" required>{{ trans('pages.leave_general.name') }}:</ui5-label>
                    <ui5-input
                        :value="addItem.name"
                        @input="addItem.name = $event.target.value"
                        type="Text"
                        name="add-name"
                        id="add-name" />
                    <div
                        class="error"
                        v-if="addItem.hasPressedSubmit && errors.addItem.name">
                        {{ errors.addItem.name }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="add-date" required>{{ trans('pages.leave_general.date') }}</ui5-label>
                    <ui5-date-picker
                        id="add-date"
                        required
                        ref="addItem.date"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="addItem.date = $event.detail.value"
                        :value="addItem.date" />
                    <div
                        class="error"
                        v-if="addItem.hasPressedSubmit && errors.addItem.date">
                        {{ errors.addItem.date }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="add-date-end">{{ trans('pages.leave_general.date_until') }}</ui5-label>
                    <ui5-date-picker
                        id="add-date-end"
                        ref="addItem.dateEnd"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="addItem.dateEnd = $event.detail.value"
                        :value="addItem.dateEnd" />
                    <div
                        class="error"
                        v-if="
                            addItem.hasPressedSubmit && errors.addItem.dateEnd
                        ">
                        {{ errors.addItem.dateEnd }}
                    </div>
                </div>
                <div class="buttons">
                    <ui5-button
                        class="addLeaveGeneralButton"
                        name="addLeaveGeneralButton"
                        @click="addLeave">
                        {{ trans('pages.leave_general.add') }}
                    </ui5-button>
                </div>
            </div>
        </div>
        <div class="rightPane">
            <div id="updateLeaveForm" v-if="updateItem">
                <h3>{{ trans('pages.leave_general.edit_general_leave_day') }}</h3>
                <div class="field">
                    <ui5-label for="update-name" required>{{ trans('pages.leave_general.name') }}</ui5-label>
                    <ui5-input
                        type="Text"
                        id="update-name"
                        :value="updateItem.name"
                        @input="updateItem.name = $event.target.value"
                        name="name" />
                    <div class="error" v-if="errors.updateItem.name">
                        {{ errors.updateItem.name }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="update-day" required>{{ trans('pages.leave_general.date') }}</ui5-label>
                    <ui5-date-picker
                        id="update-day"
                        required
                        ref="updateItem.day"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="updateItem.day = $event.detail.value"
                        :value="updateItem.day" />
                    <div class="error" v-if="errors.updateItem.day">
                        {{ errors.updateItem.day }}
                    </div>
                </div>
                <div class="buttons">
                    <ui5-button
                        :disabled="errors.updateItem.anyError"
                        name="updateLeave"
                        id="updateLeave"
                        @click="updateLeave">
                        {{ trans('pages.leave_general.save') }}
                    </ui5-button>
                    <ui5-button
                        name="cancelUpdateLeave"
                        id="cancelUpdateLeave"
                        @click="cancelUpdateForm">
                        {{ trans('pages.leave_general.cancel') }}
                    </ui5-button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import leaveGeneralApi from '../api/leaveGeneral';
import {
    showConfirmDialog,
    showInfoMessage,
    showErrorMessage,
} from '../dialogs';
import localization from '../mixins/localization';

const emptyAdd = {
    date: '',
    dateEnd: '',
    name: '',
    hasPressedSubmit: false,
};

export default {
    mixins: [localization],
    data() {
        return {
            selectedYear: new Date().getFullYear(),
            leaves: [],
            updateItem: null,
            addItem: { ...emptyAdd },
        };
    },
    mounted() {
        this.getLeave();
    },
    computed: {
        datePattern: () => 'yyyy-MM-dd',
        errors() {
            const addItem = this.addItem;

            const dateError = () => {
                if (!addItem.date) {
                    return this.trans('pages.leave_general.required');
                }

                if (this.$refs['addItem.date'].valueState == 'Error') {
                    return this.trans('pages.leave_general.wrong_date_format');
                }

                return null;
            };

            const dateEndError = () => {
                if (!addItem.dateEnd) {
                    return null;
                }
                if (this.$refs['addItem.dateEnd'].valueState == 'Error') {
                    return this.trans('pages.leave_general.wrong_date_format');
                }
                if (
                    this.$refs['addItem.date'].dateValue >
                    this.$refs['addItem.dateEnd'].dateValue
                ) {
                    return this.trans('pages.leave_general.must_be_later_or_equal', { date: this.addItem.date });
                }
                return null;
            };

            const anyError = (obj) => {
                return Object.values(obj).some((x) => x != null);
            };

            const addItemResult = {
                name: addItem.name.length ? null : this.trans('pages.leave_general.required'),
                date: dateError(),
                dateEnd: dateEndError(),
            };

            const dayError = () => {
                if (!this.updateItem.day) {
                    return this.trans('pages.leave_general.required');
                }

                if (
                    this.$refs['updateItem.day'] &&
                    this.$refs['updateItem.day'].valueState == 'Error'
                ) {
                    return this.trans('pages.leave_general.wrong_date_format');
                }

                return null;
            };

            const updateItemResult = {
                name:
                    !this.updateItem || this.updateItem.name.length
                        ? null
                        : this.trans('pages.leave_general.required'),
                day: this.updateItem ? dayError() : null,
            };

            return {
                addItem: {
                    ...addItemResult,
                    anyError: anyError(addItemResult),
                },
                updateItem: {
                    ...updateItemResult,
                    anyError: anyError(updateItemResult),
                },
            };
        },
    },
    methods: {
        shiftDateForLeaveList(direction) {
            this.selectedYear += direction;
            this.getLeave();
        },
        getLeave() {
            leaveGeneralApi.fetchForYear(this.selectedYear).then((response) => {
                this.leaves = response.data.data;
            });
        },
        prepareUpdateForm(item) {
            this.updateItem = { ...item };
        },
        cancelUpdateForm() {
            this.updateItem = null;
        },
        deleteLeave(leave, leaveIndex) {
            const id = leave.id;
            showConfirmDialog({
                title: this.trans('pages.leave_general.delete_leave_day'),
                content: this.trans('pages.leave_general.confirm_delete'),
                confirm: () => {
                    leaveGeneralApi
                        .remove(id)
                        .then(() => {
                            this.$delete(this.leaves, leaveIndex);
                            showInfoMessage(this.trans('pages.leave_general.leave_day_deleted'));
                        })
                        .catch(() => {
                            showErrorMessage(this.trans('pages.leave_general.leave_day_not_deleted'));
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans('pages.leave_general.yes'),
                cancelButton: this.trans('pages.leave_general.no'),
            });
        },
        updateLeave() {
            if (this.errors.updateItem.anyError) {
                return showErrorMessage(this.trans('pages.leave_general.fix_input_errors'));
            }
            leaveGeneralApi
                .update(
                    this.updateItem.id,
                    this.updateItem.day,
                    this.updateItem.name
                )
                .then(() => {
                    this.updateItem = null;
                    this.getLeave();
                    showInfoMessage(this.trans('pages.leave_general.leave_day_modified'));
                })
                .catch(() => {
                    showErrorMessage(this.trans('pages.leave_general.leave_day_not_modified'));
                });
        },
        addLeave() {
            this.addItem.hasPressedSubmit = true;
            if (this.errors.addItem.anyError) {
                return showErrorMessage(this.trans('pages.leave_general.fix_input_errors'));
            }
            leaveGeneralApi
                .add(this.addItem.date, this.addItem.dateEnd, this.addItem.name)
                .then(() => {
                    showInfoMessage(this.trans('pages.leave_general.leave_day_saved'));
                    this.getLeave();
                    this.addItem = { ...emptyAdd };
                })
                .catch(() => {
                    showErrorMessage(this.trans('pages.leave_general.leave_day_not_saved'));
                });
        },
    },
};
</script>
